import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Col,
  Form,
  Row,
  Segmented,
  Typography,
} from 'antd';
import styled from 'styled-components';
import { docWidth } from 'utils/responsive';
import { CameraOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import AvatarUploader from 'components/AvatarUploader';
import Uploader from 'components/Uploader';
import PicturesWall from 'components/PicturesWall';
import CargaDocumento from './CargaDocumento';

const width = docWidth * 0.35;
const extMap = {
  'vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
};

export const getTypeAndExtension = (base64) => {
  const type = base64.match(/:.+;/)[0].replace(/(:|;)/g, '');
  const [, ext] = type.split('/');
  return [type, extMap[ext] || ext];
};

/**
 * @param {string} base64
 * @param {string} fileName
 * @param {"image/png" | "application/pdf"} type
 * @returns
 */
export const dataUrlToFile = async (base64, fileName, _type = 'image/png') => {
  const res = await fetch(base64);
  const blob = await res.blob();
  const [type, ext] = getTypeAndExtension(base64);
  return new File([blob], `${fileName}.${extMap[ext] || ext}`, { type: _type || type });
};

const Camera = ({
  onSnapshot,
  disabled,
  photo: _photo,
  uploadMode,
  defualtMode,
  multiple,
}) => {
  const form = Form.useFormInstance();
  const [height, setHeight] = useState(0);
  const [streaming, setStreaming] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [mode, setMode] = useState(defualtMode);

  const canvas = useRef();
  const video = useRef();
  const photo = useRef();

  const clearPhoto = () => photo.current?.setAttribute('src', null);

  const takeSnapshot = async () => {
    const context = canvas.current.getContext('2d');
    if (width && height) {
      canvas.current.width = width;
      canvas.current.height = height;
      context.drawImage(video.current, 0, 0, width, height);

      const dataUrl = canvas.current.toDataURL('image/png');
      const file = await dataUrlToFile(dataUrl, 'fotografia');
      photo.current.setAttribute('src', dataUrl);
      form.setFieldsValue({ archivo: { file } });
      if (onSnapshot) onSnapshot(dataUrl, file);
    } else {
      clearPhoto();
    }
  };

  const setup = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      video.current.srcObject = stream;
    } catch (error) {
      return;
    }
    video.current.play();

    video.current.addEventListener(
      'canplay',
      () => {
        if (!streaming) {
          let _height = video.current.videoHeight / (video.current.videoWidth / width);

          if (Number.isNaN(_height)) {
            _height = width / (4 / 3);
          }

          video.current.setAttribute('width', width);
          video.current.setAttribute('height', _height);
          canvas.current.setAttribute('width', width);
          canvas.current.setAttribute('height', _height);
          setHeight(_height);
          setStreaming(true);
        }
      },
      false,
    );
  };

  useEffect(() => {
    if (hasPermission) setup();
  }, [hasPermission]);

  useEffect(() => {
    setup();

    let permission = null;
    navigator.permissions.query({ name: 'camera' }).then((_permission) => {
      setHasPermission(_permission.state === 'granted');
      permission = _permission;
      permission.onchange = ((e) => {
        if (e.type === 'change') {
          setHasPermission(e.target.state === 'granted');
        }
      });
    });
    return () => {
      if (permission) {
        permission.onchange = null;
      }
    };
  }, []);

  if (disabled) {
    if (!_photo) return null;

    return (
      <Container>
        <Content $visible>
          <CargaDocumento
            disabled={disabled}
            file={_photo}
            multiple={multiple}
          />
        </Content>
      </Container>
    );
  }

  const limitMB = 20;
  const allowedExtensions = ['pdf', 'docx', 'doc', 'jpeg', 'jpg', 'png', 'cvs', 'xlsx'];

  return (
    <Container $hasPermission={hasPermission}>
      <Col span={24} style={{ marginBottom: 20 }}>
        <Segmented
          size="large"
          value={mode}
          onChange={setMode}
          options={[
            {
              icon: <CameraOutlined />,
              label: 'Tomar fotografía',
              value: 'capture',
            },
            {
              icon: <UploadOutlined />,
              label: `Cargar ${uploadMode === 'avatar' ? 'fotografía' : 'documento'}`,
              value: 'upload',
            },
          ]}
        />
      </Col>
      <Content className="camera" $visible={mode === 'capture'}>
        <Row gutter={10}>
          <Col>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video ref={video}>
              Video stream not available.
            </video>
          </Col>
          <Row gutter={10}>
            <Col>
              <Button disabled={!hasPermission || disabled} onClick={takeSnapshot}>
                Capturar
                <CameraOutlined />
              </Button>
            </Col>
            <Col>
              <Button disabled={!hasPermission || disabled} onClick={clearPhoto}>
                Limpiar
                <DeleteOutlined />
              </Button>
            </Col>
          </Row>
        </Row>
        <div className="output">
          <img ref={photo} alt="" />
        </div>
        <canvas ref={canvas} />
        {!hasPermission && (
          <Typography.Text>
            Permiso denegado. Permita a la aplicación usar su cámara.
            <br />
            Si ya concedió el permiso y continua viendo este mensaje recargue la página.
          </Typography.Text>
        )}
      </Content>
      <Content $visible={mode === 'upload'}>
        <Col xs={24} sm={24} md={uploadMode === 'avatar' ? 8 : 24}>
          {uploadMode === 'avatar' && (
            <AvatarUploader
              size={width}
              shape="square"
              formItemName="archivo"
              disabled={disabled}
              onRemove={clearPhoto}
              aspectRatio={4 / 3}
              rules={[{ required: true }]}
            />
          )}
          {uploadMode === 'uploader' && !multiple && (
            <Uploader
              title="Documento Dígital"
              limitMB={limitMB}
              formItemName="archivo"
              allowedExtensions={allowedExtensions}
              rules={[{ required: true }]}
              allowPreview
              fullSize
            />
          )}
          {uploadMode === 'uploader' && multiple && (
            <PicturesWall
              label="Documentos Dígitales"
              name="archivo"
              limitMB={limitMB}
              allowedExtensions={allowedExtensions}
              rules={[{ required: true }]}
            />
          )}
        </Col>
      </Content>
    </Container>
  );
};

const Container = styled.div`
  video {
    border: 1px solid black;
    box-shadow: 2px 2px 3px black;
    width: ${width}px;
  }

  .output {
    display: inline-block;
    vertical-align: top;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
  }

  #photo {
    border: 1px solid black;
    box-shadow: 2px 2px 3px black;
  }

  canvas {
    display: none;
  }

  .camera {
    display: inline-block;
    position: relative;
  }

  ${(props) => !props.$hasPermission && `
    video, .output, #photo {
      display: none;
    }
  `}
`;

const Content = styled.div`
  ${(props) => !props.$visible && 'display: none !important;'}
`;

Camera.propTypes = {
  onSnapshot: PropTypes.func,
  disabled: PropTypes.bool,
  photo: PropTypes.string,
  uploadMode: PropTypes.oneOf(['avatar', 'uploader']),
  defualtMode: PropTypes.oneOf(['capture', 'upload']),
  multiple: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
};

Camera.defaultProps = {
  disabled: false,
  photo: null,
  onSnapshot: null,
  uploadMode: 'avatar',
  defualtMode: 'capture',
  multiple: false,
};

export default Camera;
